import { useEffect, useState, useCallback, useMemo } from "react";
import api, { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Layers, X, Download } from "lucide-react";
import {
  DataTable, EmptyState, IconBtn, Pagination, Pill, SearchInput, Td, Toolbar,
  relativeTime, usePagedRows,
} from "@/components/ui/kit";

const TEMPLATE_COLUMNS = [
  { key: "name", label: "Template" },
  { key: "rules", label: "Rules", align: "right" },
  { key: "preview", label: "Preview", hide: "hidden lg:table-cell" },
  { key: "updated", label: "Updated", align: "right", hide: "hidden md:table-cell" },
  { key: "actions", label: "Actions", align: "right" },
];

const previewOf = (t) =>
  (t.rules || [])
    .slice(0, 4)
    .map((r) =>
      r.category === "hyperlink" ? `${r.old_value} [link]→${r.new_value}`
        : r.category === "ocr_skip" ? `[OCR skip] ${r.old_value}`
        : `${r.old_value}→${r.new_value || "∅"}`)
    .join("  ·  ") + ((t.rules?.length || 0) > 4 ? "  …" : "");

const newPair = (key = `p${Date.now()}`) => ({ _k: key, old_value: "", new_value: "", category: "brand", old_url: "", new_url: "" });
const emptyForm = { name: "", pairs: [newPair("p0")] };

export const RuleTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [dragOverId, setDragOverId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/rule-templates");
      setTemplates(data);
    } catch (e) {
      console.error("templates load failed", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setPair = (i, field, val) =>
    setForm((f) => ({ ...f, pairs: f.pairs.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)) }));
  const addPair = () => setForm((f) => ({ ...f, pairs: [...f.pairs, newPair()] }));
  const removePair = (i) => setForm((f) => ({ ...f, pairs: f.pairs.filter((_, idx) => idx !== i) }));

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (t) => {
    if (t.source === "channel_scanner") { window.location.href = `/channel-scanner?scan=${encodeURIComponent(t.source_scan_id)}`; return; }
    setEditingId(t.id);
    setForm({
      name: t.name || "",
      pairs: (t.rules && t.rules.length
        ? t.rules.map((r, i) => ({ _k: `e${i}`, old_value: r.old_value, new_value: r.new_value || "", category: r.category || "brand", old_url: r.old_url || "", new_url: r.new_url || "" }))
        : [newPair("p0")]),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    const incompleteHyperlink = form.pairs.find((p) => p.category === "hyperlink"
      && (!p.old_value.trim() || !p.old_url.trim() || !p.new_value.trim() || !p.new_url.trim()));
    if (incompleteHyperlink) return toast.error("Hyperlink rules require old and new Text + URL values");
    const rules = form.pairs.filter((p) => p.old_value.trim()).map((p) => ({ old_value: p.old_value, new_value: p.new_value, category: p.category || "brand", old_url: p.old_url || "", new_url: p.new_url || "" }));
    if (rules.length === 0) return toast.error("Add at least one rule (old → new)");
    try {
      if (editingId) {
        await api.put(`/rule-templates/${editingId}`, { name: form.name.trim(), rules });
        toast.success("Template updated");
      } else {
        await api.post("/rule-templates", { name: form.name.trim(), rules });
        toast.success("Template created");
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to save template"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/rule-templates/${id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete"));
    }
  };

  const dropRule = async (event, template) => {
    event.preventDefault();
    setDragOverId(null);
    if (template.source === "channel_scanner") return toast.info("Review scanner sets in Channel Scanner to preserve preview protections");
    let rule;
    try {
      rule = JSON.parse(event.dataTransfer.getData("application/x-clonecast-rule"));
    } catch {
      return;
    }
    if (!rule?.old_value) return;
    const existing = template.rules || [];
    if (existing.some((item) => item.old_value === rule.old_value
      && (item.new_value || "") === (rule.new_value || "")
      && (item.category || "brand") === (rule.category || "brand")
      && (item.old_url || "") === (rule.old_url || "")
      && (item.new_url || "") === (rule.new_url || ""))) {
      return toast.info(`That rule is already in “${template.name}”`);
    }
    try {
      await api.put(`/rule-templates/${template.id}`, {
        rules: [...existing, { old_value: rule.old_value, new_value: rule.new_value || "", category: rule.category || "brand", old_url: rule.old_url || "", new_url: rule.new_url || "" }],
      });
      toast.success(`Rule added to “${template.name}”`);
      load();
    } catch (error) {
      toast.error(errMsg(error, "Could not add rule to template"));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      String(t.name || "").toLowerCase().includes(q) || previewOf(t).toLowerCase().includes(q));
  }, [templates, search]);

  useEffect(() => { setPage(0); }, [search, pageSize]);
  const paged = usePagedRows(filtered, { page, pageSize });

  return (
    <div className="space-y-4" data-testid="rule-templates-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 label-caps">
          <Layers className="h-3.5 w-3.5" /> Rule Templates ({templates.length})
        </div>
        <Button size="sm" onClick={openCreate} className="bg-[rgb(var(--c-violet-rgb)/0.15)] text-[var(--c-violet)] border border-[rgb(var(--c-violet-rgb)/0.30)] hover:bg-[rgb(var(--c-violet-rgb)/0.25)] font-semibold" data-testid="add-template-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Template
        </Button>
      </div>

      {templates.length > 0 && (
        <Toolbar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search templates and their rules…" testid="templates-search" />
        </Toolbar>
      )}

      <DataTable
        columns={TEMPLATE_COLUMNS}
        testid="templates-table"
        minWidth="min-w-[620px]"
        empty={
          paged.slice.length === 0 ? (
            <EmptyState
              icon={Layers}
              testid="no-templates"
              title={templates.length === 0 ? "No templates yet" : "No templates match this search"}
              hint={templates.length === 0 ? "Group replacement rules into a reusable set you can apply to any job in one click." : undefined}
            />
          ) : null
        }
      >
        {paged.slice.map((t) => {
          const active = dragOverId === t.id;
          return (
            <tr
              key={t.id}
              onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; }}
              onDragEnter={() => setDragOverId(t.id)}
              onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragOverId(null); }}
              onDrop={(event) => dropRule(event, t)}
              className="border-b border-[var(--tg-line-soft)] hover:bg-[var(--tg-hover)] transition-colors"
              style={active ? { background: "rgb(var(--c-violet-rgb)/0.12)", outline: "1px solid var(--c-violet)" } : undefined}
              data-testid={`template-${t.id}`}
            >
              <Td>
                <div className="flex items-center gap-2 min-w-0">
                  {active && <Download className="h-3.5 w-3.5 text-[var(--c-violet)] shrink-0 animate-bounce" />}
                  <span className="font-semibold text-sm truncate max-w-[220px]" title={t.name} data-testid={`template-name-${t.id}`}>{t.name}</span>
                </div>
                <div className="text-[10px] text-[var(--tg-muted)] mt-1">{t.source === "channel_scanner" ? `Channel Scanner · ${t.source_channel} · ${t.scope} scope · ${new Date(t.created_at).toLocaleDateString()}` : "Manual"}</div>
              </Td>
              <Td align="right">
                <Pill tone="var(--c-violet)" size="sm">{t.rules?.length || 0}</Pill>
              </Td>
              <Td className="hidden lg:table-cell">
                <div className="font-mono text-[11px] text-[var(--tg-faint)] truncate max-w-[420px]" title={previewOf(t)}>
                  {active ? "Drop here to add this rule" : previewOf(t)}
                </div>
              </Td>
              <Td align="right" className="hidden md:table-cell text-[11px] text-[var(--tg-faint)] whitespace-nowrap">
                {relativeTime(t.updated_at || t.created_at) || "—"}
              </Td>
              <Td align="right">
                <div className="flex items-center justify-end gap-1">
                  <IconBtn icon={Pencil} title="Edit template" onClick={() => openEdit(t)} testid={`edit-template-${t.id}`} />
                  <IconBtn icon={Trash2} title="Delete template" tone="var(--c-rose)" onClick={() => remove(t.id)} testid={`delete-template-${t.id}`} />
                </div>
              </Td>
            </tr>
          );
        })}
      </DataTable>

      {filtered.length > pageSize && (
        <Pagination
          page={paged.safePage} pageCount={paged.pageCount} total={paged.total}
          from={paged.from} to={paged.to} pageSize={pageSize} onPageSize={setPageSize}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(paged.pageCount - 1, p + 1))}
          testid="templates-pagination"
        />
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
        <DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="font-head">{editingId ? "Edit Template" : "Create Template"}</DialogTitle>
            <DialogDescription className="text-[var(--tg-faint)]">
              A named set of replacement rules you can apply to any job in one click. Matching is case-insensitive.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="label-caps">Template Name</Label>
              <Input data-testid="template-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Rebrand links" className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-violet)]" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Rules (old → new)</Label>
              <div className="space-y-2">
                {form.pairs.map((p, i) => (
                  <div key={p._k} className={`rounded-lg border p-3 space-y-3 ${p.category === "hyperlink" ? "border-[rgb(var(--c-violet-rgb)/0.40)] bg-[rgb(var(--c-violet-rgb)/0.05)]" : "border-[var(--tg-border)] bg-black/10"}`} data-testid={`template-pair-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                    <Select value={p.category || "brand"} onValueChange={(value) => setPair(i, "category", value)}>
                      <SelectTrigger className="bg-[var(--tg-bg)] border-[var(--tg-border)] text-xs w-[150px]" data-testid={`template-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
                        <SelectItem value="username">Username</SelectItem>
                        <SelectItem value="url">URL</SelectItem>
                        <SelectItem value="brand">Brand</SelectItem>
                        <SelectItem value="hyperlink">Hyperlink</SelectItem>
                        <SelectItem value="ocr_skip">OCR Image Skip</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.pairs.length > 1 && (
                      <button onClick={() => removePair(i)} className="text-[var(--tg-faint)] hover:text-[var(--c-rose)]" data-testid={`template-remove-${i}`}><X className="h-4 w-4" /></button>
                    )}
                    </div>
                    {p.category === "hyperlink" ? (
                      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                        <div className="space-y-2"><div className="text-[10px] uppercase tracking-wider text-[var(--c-rose)] font-semibold">Old hyperlink</div><Input value={p.old_value} onChange={(e) => setPair(i, "old_value", e.target.value)} placeholder="Old visible text" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono text-xs" data-testid={`template-old-${i}`} /><Input value={p.old_url || ""} onChange={(e) => setPair(i, "old_url", e.target.value)} placeholder="Old URL — https://…" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono text-xs" data-testid={`template-old-url-${i}`} /></div>
                        <span className="text-[var(--tg-faint)] text-center">→</span>
                        <div className="space-y-2"><div className="text-[10px] uppercase tracking-wider text-[var(--c-emerald)] font-semibold">New hyperlink</div><Input value={p.new_value} onChange={(e) => setPair(i, "new_value", e.target.value)} placeholder="New visible text" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono text-xs" data-testid={`template-new-${i}`} /><Input value={p.new_url || ""} onChange={(e) => setPair(i, "new_url", e.target.value)} placeholder="New URL — https://…" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono text-xs" data-testid={`template-new-url-${i}`} /></div>
                      </div>
                    ) : p.category === "ocr_skip" ? (
                      <div className="space-y-2"><div className="text-[10px] uppercase tracking-wider text-[var(--c-amber)] font-semibold">Skip image when OCR finds</div><Input value={p.old_value} onChange={(e) => setPair(i, "old_value", e.target.value)} placeholder="Text to detect in the image" className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-amber)] font-mono text-xs" data-testid={`template-old-${i}`} /><p className="text-[10px] text-[var(--tg-faint)]">Case-insensitive. The entire post (or album) is skipped; no replacement value is needed.</p></div>
                    ) : (
                      <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center"><Input value={p.old_value} onChange={(e) => setPair(i, "old_value", e.target.value)} placeholder="old value" className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-violet)] font-mono text-xs" data-testid={`template-old-${i}`} /><span className="text-[var(--tg-faint)]">→</span><Input value={p.new_value} onChange={(e) => setPair(i, "new_value", e.target.value)} placeholder="new value (blank = remove)" className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-violet)] font-mono text-xs" data-testid={`template-new-${i}`} /></div>
                    )}
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={addPair} className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="template-add-pair"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add rule</Button>
            </div>
            <Button onClick={save} className="w-full bg-[var(--c-violet)] hover:bg-[var(--c-violet-hover)] text-white font-semibold" data-testid="save-template-btn">
              {editingId ? "Save Changes" : "Create Template"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
